import { useEffect, useRef, useState } from "react";
import { MoreVertical, Flag, Ban, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const REASONS = [
  { value: "harassment", label: "Harassment or threats" },
  { value: "scam", label: "Scam or fraud" },
  { value: "spam", label: "Spam / irrelevant" },
  { value: "confidentiality_breach", label: "Shared my manuscript without permission" },
  { value: "impersonation", label: "Impersonation" },
  { value: "inappropriate_content", label: "Inappropriate content" },
  { value: "other", label: "Something else" },
];

/**
 * Compact "..." menu for a user context: Report + Block/Unblock.
 * Used in message thread headers and public profile pages.
 */
export default function ReportBlockMenu({ targetUserId, targetDisplayName, context, size = "md" }) {
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState(null); // 'report' | 'block' | 'unblock'
  const [isBlocked, setIsBlocked] = useState(null); // null = unknown/loading
  const ref = useRef(null);

  useEffect(() => {
    // Cheap probe: fetch my blocks once and check membership. Result is cached in state.
    if (!targetUserId) return;
    let alive = true;
    api.get("/moderation/blocks")
      .then((r) => {
        if (!alive) return;
        setIsBlocked((r.data || []).some((b) => b.blocked_user_id === targetUserId));
      })
      .catch(() => setIsBlocked(false));
    return () => { alive = false; };
  }, [targetUserId]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", onDoc);
    return () => window.removeEventListener("mousedown", onDoc);
  }, [open]);

  const closeModal = () => setModal(null);

  const submitBlock = async () => {
    try {
      await api.post("/moderation/blocks", { blocked_user_id: targetUserId });
      setIsBlocked(true);
      toast.success(`${targetDisplayName || "User"} blocked. They won't be able to message you.`);
      closeModal();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitUnblock = async () => {
    try {
      await api.delete(`/moderation/blocks/${targetUserId}`);
      setIsBlocked(false);
      toast.success(`${targetDisplayName || "User"} unblocked.`);
      closeModal();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const btnClass =
    size === "sm"
      ? "rounded-full p-1.5 text-muted-foreground hover:bg-muted transition-colors"
      : "rounded-full p-2 text-muted-foreground hover:bg-muted transition-colors";

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={btnClass}
        aria-label={`Actions for ${targetDisplayName || "this user"}`}
        data-testid="report-block-menu-trigger"
      >
        <MoreVertical size={16} />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-1 z-30 min-w-[220px] rounded-xl border bg-card shadow-lg overflow-hidden"
          data-testid="report-block-menu"
        >
          <button
            type="button"
            onClick={() => { setOpen(false); setModal("report"); }}
            className="w-full text-left px-4 py-2.5 text-sm hover:bg-muted flex items-center gap-2"
            data-testid="report-user-btn"
          >
            <Flag size={14} className="text-[hsl(var(--sb-clay))]" /> Report {targetDisplayName || "user"}
          </button>
          {isBlocked ? (
            <button
              type="button"
              onClick={() => { setOpen(false); setModal("unblock"); }}
              className="w-full text-left px-4 py-2.5 text-sm hover:bg-muted flex items-center gap-2 border-t"
              data-testid="unblock-user-btn"
            >
              <CheckCircle2 size={14} className="text-[hsl(var(--sb-forest-2))]" /> Unblock user
            </button>
          ) : (
            <button
              type="button"
              onClick={() => { setOpen(false); setModal("block"); }}
              className="w-full text-left px-4 py-2.5 text-sm hover:bg-muted flex items-center gap-2 border-t"
              data-testid="block-user-btn"
            >
              <Ban size={14} className="text-destructive" /> Block user
            </button>
          )}
        </div>
      )}

      {modal === "report" && (
        <ReportModal
          targetDisplayName={targetDisplayName}
          onClose={closeModal}
          onSubmitted={closeModal}
          targetUserId={targetUserId}
          context={context}
        />
      )}
      {modal === "block" && (
        <ConfirmModal
          testid="block-user-confirm"
          title="Block this user?"
          body={
            <p>
              They will no longer be able to send you messages or Coffee Chat
              requests. They will not be notified that you blocked them.
            </p>
          }
          confirmLabel="Block user"
          onCancel={closeModal}
          onConfirm={submitBlock}
          destructive
        />
      )}
      {modal === "unblock" && (
        <ConfirmModal
          testid="unblock-user-confirm"
          title={`Unblock ${targetDisplayName || "this user"}?`}
          body={<p>They'll be able to message you again. You can block them again at any time.</p>}
          confirmLabel="Unblock"
          onCancel={closeModal}
          onConfirm={submitUnblock}
        />
      )}
    </div>
  );
}

function ReportModal({ targetUserId, targetDisplayName, onClose, onSubmitted, context }) {
  const [reason, setReason] = useState("harassment");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/moderation/reports", {
        reported_user_id: targetUserId,
        reason,
        details,
        context: context || null,
      });
      toast.success("Report received. Our admins will review it. Thank you for helping keep Last Bookmark safe.");
      onSubmitted();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center sm:p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      data-testid="report-user-modal"
    >
      <div className="absolute inset-0 bg-foreground/50 backdrop-blur-[2px]" onClick={onClose} />
      <form
        onSubmit={submit}
        className="relative w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl border bg-[hsl(var(--sb-cream))] shadow-2xl max-h-[92vh] sm:max-h-[calc(100vh-4rem)] flex flex-col"
      >
        <div className="flex-1 overflow-y-auto p-6 sm:p-7">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-[hsl(var(--sb-clay))]">
                <Flag size={12} /> Report
              </div>
              <h2 className="mt-2 font-serif text-2xl leading-tight">
                Report {targetDisplayName || "user"}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Admins review every report. The reported user is never told who reported them.
              </p>
            </div>
            <button type="button" onClick={onClose} className="p-1 -mt-1 -mr-1" aria-label="Close">
              <X size={16} />
            </button>
          </div>
          <div className="mt-5">
            <label className="text-sm font-medium mb-2 block">Reason</label>
            <div className="space-y-1.5">
              {REASONS.map((r) => (
                <label key={r.value} className="flex items-start gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="report-reason"
                    value={r.value}
                    checked={reason === r.value}
                    onChange={() => setReason(r.value)}
                    className="mt-1 accent-[hsl(var(--sb-forest))]"
                    data-testid={`report-reason-${r.value}`}
                  />
                  {r.label}
                </label>
              ))}
            </div>
          </div>
          <div className="mt-4">
            <label htmlFor="report-details" className="text-sm font-medium mb-1.5 block">
              Details <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            <textarea
              id="report-details"
              rows={4}
              className="sb-input"
              placeholder="What happened? Include any relevant messages or context."
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              maxLength={4000}
              data-testid="report-details"
            />
          </div>
        </div>
        <div
          className="flex flex-col-reverse sm:flex-row justify-end gap-2 px-6 sm:px-7 py-4 border-t bg-[hsl(var(--sb-cream))] rounded-b-2xl"
          style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
        >
          <button type="button" onClick={onClose} className="btn-ghost border w-full sm:w-auto">Cancel</button>
          <button
            type="submit"
            className="btn-primary w-full sm:w-auto"
            disabled={busy}
            data-testid="report-submit"
          >
            <Flag size={14} /> {busy ? "Sending…" : "Submit report"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ConfirmModal({ title, body, confirmLabel, onCancel, onConfirm, destructive, testid }) {
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid={testid}>
      <div className="absolute inset-0 bg-foreground/50 backdrop-blur-[2px]" onClick={onCancel} />
      <div className="relative w-full max-w-sm rounded-2xl border bg-[hsl(var(--sb-cream))] shadow-2xl">
        <div className="p-6">
          <h2 className="font-serif text-xl">{title}</h2>
          <div className="mt-3 text-sm text-foreground/90">{body}</div>
          <div className="mt-5 flex justify-end gap-2">
            <button type="button" onClick={onCancel} className="btn-ghost border">Cancel</button>
            <button
              type="button"
              onClick={onConfirm}
              className={
                destructive
                  ? "inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium bg-destructive text-destructive-foreground hover:opacity-90"
                  : "btn-primary"
              }
              data-testid={`${testid}-confirm`}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
